<div class="legal">
  <h1>Privacy Policy</h1>
  <p class="effective">Effective: October 14, 2026 &middot; Version 1.4</p>

  <section>
    <h2>1. Information We Collect</h2>
    <h3>Account Information</h3>
    <p>
      When you register, we collect your username, email address, and password (stored as a bcrypt hash). We also ask
      for your birth month and year to confirm you meet the 13+ minimum age requirement; we keep only your age band
      (13–15, 16–17 or 18+). For players under 18 we also keep the month they turn 18, from which the birth month can be
      worked out; we erase it once they turn 18, so adults keep no birth data. Social login (Sign in with Apple, Google,
      Spotify, or TikTok) provides a platform identifier and, where the platform shares them, your name and email
      address.
    </p>

    <h3>Profile Information</h3>
    <p>You may optionally choose a profile image from a curated preset selection and update your username.</p>

    <h3>Activity Data</h3>
    <p>
      We record your interactions with the Service: duels joined, tracks submitted, votes cast, listening activity, and
      follow relationships. This data powers rankings, recommendations, and achievements.
    </p>

    <h3>Connected Services</h3>
    <p>
      If you connect Spotify, Apple Music, SoundCloud, or TikTok, we receive limited data from those platforms (such as
      your top artists or liked tracks) to build genre preferences. We store OAuth tokens securely and do not access
      more data than the requested scope.
    </p>

    <h3>Technical Data</h3>
    <p>
      We collect standard server logs (IP address, browser type, request timestamps) for security and debugging
      purposes. Our iOS and Android apps also record in-app usage events (such as screens viewed and games started) to
      improve the Service, and attach your app version, device model, and operating-system version to feedback you send.
      We do not use advertising identifiers and do not track you across other companies' apps or websites.
    </p>
  </section>

  <section>
    <h2>2. How We Use Your Information</h2>
    <ul>
      <li>
        <strong>Service operation</strong> &mdash; authentication, track playback, voting, rankings, and notifications
      </li>
      <li><strong>Personalization</strong> &mdash; genre recommendations, taste-matched duels, Music DNA profiles</li>
      <li>
        <strong>Communication</strong> &mdash; password resets, verification emails, weekly recap emails, and in-app notifications
        (you control email types in account settings)
      </li>
      <li><strong>Safety</strong> &mdash; rate limiting, abuse detection, content moderation</li>
      <li>
        <strong>Improvement</strong> &mdash; aggregated analytics to understand feature usage and improve the Service
      </li>
    </ul>
  </section>

  <section>
    <h2>3. Information Sharing</h2>
    <p>We do not sell your personal information. We share data only in these cases:</p>
    <ul>
      <li>
        <strong>Public profile</strong> &mdash; your username, profile image, stats, and achievements are publicly visible
      </li>
      <li>
        <strong>Service providers</strong> &mdash; SendGrid (email delivery), AWS S3 (file storage), and error tracking services
        process data on our behalf
      </li>
      <li>
        <strong>Legal requirements</strong> &mdash; we may disclose data when required by law or to protect the safety of
        users
      </li>
    </ul>
  </section>

  <section>
    <h2>4. Data Retention</h2>
    <p>
      Account data is retained while your account is active. If you request account deletion, we soft-delete your
      account (hiding it from public view) and retain minimal data for legal compliance. Tracks, votes, and duel results
      associated with your account remain for historical integrity of completed duels.
    </p>
  </section>

  <section>
    <h2>5. Cookies and Local Storage</h2>
    <p>
      We use <code>localStorage</code> to store your authentication token and user preferences. We don't set third-party tracking
      cookies ourselves; the embedded YouTube player is provided by Google and may set its own cookies (see YouTube API Services
      below).
    </p>
  </section>

  <section>
    <h2>6. YouTube API Services</h2>
    <p>
      Beat Duel uses YouTube API Services to play songs through YouTube's embedded player. We use YouTube API data
      (video titles, thumbnails and durations) only to show and play tracks, and we never access your YouTube account.
      When a song plays, YouTube and Google may collect information, including through cookies and similar technologies
      on your device, as described in the
      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>.
      Your use of YouTube is also governed by the
      <a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer">YouTube Terms of Service</a>.
    </p>
  </section>

  <section>
    <h2>7. Push Notifications</h2>
    <p>
      You may opt in to browser push notifications. Subscription data (endpoint and encryption keys) is stored on our
      servers. You can unsubscribe at any time from Account &gt; Notifications. If you allow notifications in our iOS or
      Android app, we store the device push token issued by Apple or Google so we can deliver them; you can turn them
      off in your device settings at any time.
    </p>
  </section>

  <section>
    <h2>8. Email Communications</h2>
    <p>
      We send transactional emails (password reset, verification) that cannot be opted out of. Weekly recap,
      new-follower, and vote-milestone emails can each be toggled on or off in Account Settings. Every non-critical
      email includes a one-click unsubscribe link. We enforce a daily cap on non-critical emails per user.
    </p>
  </section>

  <section>
    <h2>9. Security</h2>
    <p>
      We use HTTPS for all connections, bcrypt for password hashing, and OAuth2 Bearer tokens for API authentication.
      File uploads are MIME-validated server-side. We implement rate limiting to prevent abuse.
    </p>
  </section>

  <section>
    <h2>10. Children's Privacy</h2>
    <p>
      The Service is not intended for children under 13. We do not knowingly collect personal information from children
      under 13. If we learn that we have collected such data, we will delete it promptly.
    </p>
  </section>

  <section>
    <h2>11. Your Rights</h2>
    <p>You may:</p>
    <ul>
      <li>Access and update your personal information via Account Settings</li>
      <li>Download your data by contacting us</li>
      <li>Request account deletion by contacting us</li>
      <li>Unsubscribe from non-essential emails at any time</li>
      <li>Disconnect third-party services (Spotify, SoundCloud) from Account Settings</li>
    </ul>
  </section>

  <section>
    <h2>12. Changes to This Policy</h2>
    <p>
      We may update this policy. Material changes will be communicated via the Service or email. Continued use after
      changes constitutes acceptance.
    </p>
  </section>

  <section>
    <h2>13. Contact</h2>
    <p>Privacy questions? Email <a href="mailto:hello@beatduel.com">hello@beatduel.com</a>.</p>
  </section>
</div>

<style>
  .legal {
    max-width: 720px;
    margin: 0 auto;
    padding: 2rem 1rem;
    color: var(--text-secondary);
    line-height: 1.7;
  }
  h1 {
    font-size: 1.5rem;
    color: var(--text);
    margin-bottom: 0.25rem;
  }
  .effective {
    font-size: 0.85rem;
    color: var(--text-muted);
    margin-bottom: 2rem;
  }
  h2 {
    font-size: 1rem;
    color: var(--text);
    margin: 1.5rem 0 0.5rem;
    font-weight: 600;
  }
  h3 {
    font-size: 0.9rem;
    color: var(--text);
    margin: 0.75rem 0 0.25rem;
    font-weight: 500;
  }
  p {
    margin: 0.5rem 0;
  }
  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }
  li {
    margin: 0.35rem 0;
  }
  code {
    background: var(--surface-alt, rgba(255, 255, 255, 0.05));
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
    font-size: 0.85em;
  }
  a {
    color: var(--cyan);
  }
  section {
    margin-bottom: 0.5rem;
  }
</style>
